import type { CreditCardWithCurrencies } from '@contracts/payment/CreditCard';
import { TransactionCurrency } from '@contracts/payment/Currency';
import { CreditCardLogo } from '@src/clients/financialSettings/CreditCardLogo';
import { DeleteCreditCardModal } from '@src/clients/financialSettings/DeleteCreditCardModal';
import { Button } from '@src/common/components/button/Button';
import { SimpleTable } from '@src/common/components/SimpleTable';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import { Trash01 } from '@untitled-ui/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  clientId: string;
  creditCards: CreditCardWithCurrencies[];
  onDeleteCreditCard: (args: {
    clientId: string;
    creditCardId: string;
    currency: TransactionCurrency;
  }) => void;
}

export const SavedCreditCards = ({
  clientId,
  creditCards,
  onDeleteCreditCard,
}: Props) => {
  const { t } = useTranslation();

  const handleDeleteCard = () => {
    if (!cardToDelete) return;

    const currencies = cardToDelete.supportedCurrency || [];
    const currency = currencies.includes(TransactionCurrency.CAD)
      ? TransactionCurrency.CAD
      : TransactionCurrency.USD;

    onDeleteCreditCard({
      clientId,
      creditCardId: cardToDelete.id,
      currency,
    });

    setCardToDelete(null);
  };

  const [cardToDelete, setCardToDelete] =
    useState<CreditCardWithCurrencies | null>(null);

  return (
    <>
      <SimpleTable
        columnsName={[
          t('savedCreditCards.nameOnCard'),
          t('savedCreditCards.associatedCurrency'),
          t('savedCreditCards.cardNumber'),
        ]}
        rows={creditCards.map((card) => ({
          id: `${card.id}`,
          columnsContent: [
            card.nameOnCard,
            card.supportedCurrency.join(','),
            <Stack
              key={card.id}
              direction="row"
              gap={theme.spacing['1']}
              alignItems="center"
            >
              <CreditCardLogo brand={card.brand} />
              {card.cardName}
              <Button onClick={() => setCardToDelete(card)}>
                <Trash01 />
              </Button>
            </Stack>,
          ],
        }))}
      />

      <DeleteCreditCardModal
        creditCard={cardToDelete}
        open={!!cardToDelete}
        onClose={() => setCardToDelete(null)}
        onConfirm={handleDeleteCard}
      />
    </>
  );
};
